/**
 * Edit a field from the accounts table without opening the account: click the value, change
 * it, Enter (or Save) to save, Escape to cancel. Fields the API key can't write yet show a
 * lock and say where to edit them instead (see providers/zinspector/accountWrites.ts).
 */
import { Check, Lock, Pencil } from "lucide-react";
import { useNotify, useUpdate } from "ra-core";
import { useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import {
  accountFieldWriteKind,
  accountWriteSupport,
} from "../providers/zinspector/accountWrites";
import { isFakeZinspector } from "../providers/zinspector/client";
import { ACCOUNT_STATUSES } from "../providers/zinspector/crmConfig";

export type EditorSpec =
  | { type: "text"; field: string; inputMode?: "numeric" | "text" }
  | {
      type: "select";
      field: string;
      options: { value: string; label: string }[];
    }
  | { type: "doors" }
  /** Status set by hand, or back to the one the engagement gives. */
  | { type: "status" }
  /** The next step's text and due date; a follow-up can also be marked done. */
  | { type: "nextStep" };

const NONE = "__none";

const fieldsOf = (spec: EditorSpec): string[] => {
  switch (spec.type) {
    case "doors":
      return ["doors_served", "serviceable_doors"];
    case "status":
      return ["status"];
    case "nextStep":
      return ["next_step", "next_step_due"];
    default:
      return [spec.field];
  }
};

/** What the editor starts from: a status from the engagement edits as "automatic" (""). */
const initialValue = (account: ZcrmAccount, field: string): string => {
  if (field === "status" && account.status_source !== "manual") return "";
  if (field === "next_step_due")
    return (account.next_step_due ?? "").slice(0, 10);
  return String((account as Record<string, unknown>)[field] ?? "");
};

/** Stops a click in the cell (or its popover, a React child) from opening the account. */
const stop = (event: { stopPropagation: () => void }) =>
  event.stopPropagation();

export const EditableCell = ({
  account,
  label,
  spec,
  children,
}: {
  account: ZcrmAccount;
  label: string;
  spec: EditorSpec;
  children: ReactNode;
}) => {
  const fields = fieldsOf(spec);
  const kind = accountFieldWriteKind(fields[0]);
  const canWrite =
    kind !== null && accountWriteSupport(isFakeZinspector())[kind];

  if (!canWrite) {
    return (
      <span className="group/cell inline-flex items-center gap-1">
        {children}
        <Tooltip>
          <TooltipTrigger asChild>
            <Lock
              className="size-3 text-muted-foreground opacity-0 group-hover/cell:opacity-60"
              aria-label={`${label} is edited in zInspector`}
            />
          </TooltipTrigger>
          <TooltipContent>
            Edit {label.toLowerCase()} in the zInspector dashboard: the API
            can't save it yet.
          </TooltipContent>
        </Tooltip>
      </span>
    );
  }
  return (
    <CellEditor account={account} label={label} spec={spec} fields={fields}>
      {children}
    </CellEditor>
  );
};

const CellEditor = ({
  account,
  label,
  spec,
  fields,
  children,
}: {
  account: ZcrmAccount;
  label: string;
  spec: EditorSpec;
  fields: string[];
  children: ReactNode;
}) => {
  const initial = () =>
    Object.fromEntries(fields.map((f) => [f, initialValue(account, f)]));
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();

  const send = (data: Record<string, unknown>, done: string) =>
    update(
      "companies",
      {
        id: account.id,
        data,
        previousData: Object.fromEntries(
          Object.keys(data).map((f) => [f, initialValue(account, f)]),
        ),
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(done, { type: "success" });
          setOpen(false);
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : "Saving failed", {
            type: "error",
          }),
      },
    );

  const save = () => {
    const changed = fields.some((f) => values[f] !== initialValue(account, f));
    if (!changed) {
      setOpen(false);
      return;
    }
    send(values, `${label} saved`);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setValues(initial());
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={stop}
          className="group/cell -mx-1 px-1 rounded text-left inline-flex items-center gap-1 hover:bg-accent focus-visible:outline-2"
          aria-label={`Edit ${label.toLowerCase()}`}
        >
          {children}
          <Pencil className="size-3 shrink-0 text-muted-foreground opacity-0 group-hover/cell:opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64"
        onClick={stop}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <p className="text-sm font-medium">{label}</p>
          {spec.type === "text" ? (
            <Input
              autoFocus
              aria-label={label}
              inputMode={spec.inputMode}
              value={values[spec.field]}
              onChange={(e) => setValues({ [spec.field]: e.target.value })}
            />
          ) : null}
          {spec.type === "select" ? (
            <Select
              value={values[spec.field] || NONE}
              onValueChange={(value) =>
                setValues({ [spec.field]: value === NONE ? "" : value })
              }
            >
              <SelectTrigger aria-label={label} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent onClick={stop}>
                <SelectItem value={NONE}>Not set</SelectItem>
                {spec.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {spec.type === "status" ? (
            <StatusEditor
              account={account}
              value={values.status}
              onChange={(status) => setValues({ status })}
            />
          ) : null}
          {spec.type === "nextStep" ? (
            <div className="flex flex-col gap-2">
              <Input
                autoFocus
                aria-label="Next step"
                placeholder="e.g. Send periodic pricing"
                value={values.next_step}
                onChange={(e) =>
                  setValues((v) => ({ ...v, next_step: e.target.value }))
                }
              />
              <Input
                type="date"
                aria-label="Due"
                value={values.next_step_due}
                onChange={(e) =>
                  setValues((v) => ({ ...v, next_step_due: e.target.value }))
                }
              />
              {account.next_step_source === "onboarding" ? (
                <p className="text-xs text-muted-foreground">
                  This is the onboarding playbook's to-do. Saving adds your own
                  next step, which shows first.
                </p>
              ) : null}
              {account.next_step_source === "follow-up" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isPending}
                  onClick={() =>
                    send({ next_step_done: true }, "Next step done")
                  }
                >
                  <Check /> Mark done
                </Button>
              ) : null}
            </div>
          ) : null}
          {spec.type === "doors" ? (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Served
                <Input
                  autoFocus
                  aria-label="Doors served"
                  inputMode="numeric"
                  value={values.doors_served}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, doors_served: e.target.value }))
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Total
                <Input
                  aria-label="Serviceable doors"
                  inputMode="numeric"
                  value={values.serviceable_doors}
                  onChange={(e) =>
                    setValues((v) => ({
                      ...v,
                      serviceable_doors: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};

const AUTOMATIC = "__automatic";

const StatusEditor = ({
  account,
  value,
  onChange,
}: {
  account: ZcrmAccount;
  value: string;
  onChange: (status: string) => void;
}) => {
  const fromEngagement =
    ACCOUNT_STATUSES.find((s) => s.id === account.engagement_status)?.label ??
    account.engagement_status;
  return (
    <Select
      value={value || AUTOMATIC}
      onValueChange={(next) => onChange(next === AUTOMATIC ? "" : next)}
    >
      <SelectTrigger aria-label="Status" className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent onClick={stop}>
        <SelectItem value={AUTOMATIC}>
          Automatic: {fromEngagement} (from the engagement)
        </SelectItem>
        {ACCOUNT_STATUSES.map((status) => (
          <SelectItem key={status.id} value={status.id}>
            {status.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
